import { useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';

interface Lock {
  id: number;
  prompt_id: number;
  locked_by_email: string;
  locked_by_name: string | null;
  locked_at: string;
  expires_at: string;
  is_expired: boolean;
}

interface PromptLockStatusProps {
  promptId: number | null;
  userEmail: string;
  userName?: string;
  onLockAcquired?: () => void;
  onLockLost?: () => void;
  onLockStatusChange?: (canEdit: boolean) => void;
}

export default function PromptLockStatus({
  promptId,
  userEmail,
  userName,
  onLockAcquired,
  onLockLost,
  onLockStatusChange
}: PromptLockStatusProps) {
  const [lock, setLock] = useState<Lock | null>(null);
  const [isLocked, setIsLocked] = useState(false);
  const [canEdit, setCanEdit] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Check lock status periodically
  const checkLockStatus = useCallback(async () => {
    if (!promptId) return;

    try {
      const response = await api.get(`/prompts/${promptId}/lock/status`, {
        params: { user_email: userEmail }
      });

      const { is_locked, lock: lockData, can_edit } = response.data;

      setIsLocked(is_locked);
      setLock(lockData);
      setCanEdit(can_edit);

      if (onLockStatusChange) {
        onLockStatusChange(can_edit);
      }

      // If we lost the lock, notify parent
      if (is_locked && !can_edit && lockData?.locked_by_email !== userEmail) {
        if (onLockLost) {
          onLockLost();
        }
      }
    } catch (err) {
      console.error('Failed to check lock status:', err);
    }
  }, [promptId, userEmail, onLockStatusChange, onLockLost]);

  useEffect(() => {
    checkLockStatus();

    // Check lock status every 30 seconds
    const interval = setInterval(checkLockStatus, 30000);

    return () => clearInterval(interval);
  }, [checkLockStatus]);

  const acquireLock = async () => {
    if (!promptId) return;

    try {
      setLoading(true);
      setError(null);

      const response = await api.post(`/prompts/${promptId}/lock`, {
        editor_email: userEmail,
        editor_name: userName
      });

      if (response.data.success) {
        setLock(response.data.lock);
        setIsLocked(true);
        setCanEdit(true);

        if (onLockAcquired) {
          onLockAcquired();
        }
        if (onLockStatusChange) {
          onLockStatusChange(true);
        }
      }
    } catch (err: any) {
      if (err.response?.status === 423) {
        const detail = err.response?.data?.detail;
        setError(detail?.message || 'Prompt is locked by another user');
        setCanEdit(false);
        if (onLockStatusChange) {
          onLockStatusChange(false);
        }
      } else {
        setError('Failed to acquire lock');
      }
    } finally {
      setLoading(false);
    }
  };

  const releaseLock = async () => {
    if (!promptId) return;

    try {
      setLoading(true);
      await api.delete(`/prompts/${promptId}/lock`, {
        params: { user_email: userEmail }
      });

      setLock(null);
      setIsLocked(false);
    } catch (err) {
      console.error('Failed to release lock:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // No prompt selected
  if (!promptId) {
    return null;
  }

  // If locked by another user
  if (isLocked && !canEdit) {
    return (
      <div className="p-3 bg-warning-50 border border-warning-200 rounded-lg">
        <div className="flex items-center gap-2">
          <svg className="w-5 h-5 text-warning-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
          <div className="flex-1">
            <p className="text-sm font-medium text-warning-800">
              Locked by {lock?.locked_by_name || lock?.locked_by_email}
            </p>
            <p className="text-xs text-warning-600">
              Since {lock?.locked_at ? formatTime(lock.locked_at) : 'unknown'} |
              Expires {lock?.expires_at ? formatTime(lock.expires_at) : 'unknown'}
            </p>
          </div>
          <button
            onClick={checkLockStatus}
            className="text-warning-600 hover:text-warning-800 p-1"
            title="Refresh lock status"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
        </div>
        {error && (
          <p className="mt-2 text-xs text-danger-600">{error}</p>
        )}
      </div>
    );
  }

  // If we hold the lock
  if (isLocked && canEdit && lock?.locked_by_email === userEmail) {
    return (
      <div className="p-3 bg-success-50 border border-success-200 rounded-lg">
        <div className="flex items-center gap-2">
          <svg className="w-5 h-5 text-success-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
          </svg>
          <div className="flex-1">
            <p className="text-sm font-medium text-success-800">
              You have the edit lock
            </p>
            <p className="text-xs text-success-600">
              Expires {lock?.expires_at ? formatTime(lock.expires_at) : 'unknown'}
            </p>
          </div>
          <button
            onClick={releaseLock}
            disabled={loading}
            className="text-success-600 hover:text-success-800 text-xs"
          >
            Release
          </button>
        </div>
      </div>
    );
  }

  // Not locked - show acquire lock button
  return (
    <div className="flex items-center gap-2">
      <button
        onClick={acquireLock}
        disabled={loading}
        className="btn-secondary text-sm py-1.5"
      >
        {loading ? (
          <>
            <span className="spinner w-3 h-3"></span>
            Acquiring...
          </>
        ) : (
          <>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            Lock for Editing
          </>
        )}
      </button>
      <span className="text-xs text-gray-500">
        Lock to prevent others from editing
      </span>
      {error && (
        <span className="text-xs text-danger-600">{error}</span>
      )}
    </div>
  );
}
